/**
 * Batch/Serial Helper for Simulator Workflows
 * Centralizes batch/serial logic: item type classification, API calls for
 * available batches/serials, and generation of simulator batch/serial numbers.
 */

import { getSharedRateLimiter } from './RateLimiter';
import { AuthService } from '../../../../../services/authService';

const API_BASE_URL = process.env.REACT_APP_API_URL || 'https://localhost:7141/api/v1';

const authenticatedFetch = async (url: string, options: RequestInit = {}): Promise<Response> => {
  return AuthService.authenticatedFetch(url, options);
};

// ============================================================================
// Types
// ============================================================================

export interface BatchInfo {
  batchNo: string;
  expiryDate: string | null;
  quantity: number;
  binCode?: string;
  warehouseId?: string;
}

export interface SerialInfo {
  serialNo: string;
  binCode?: string;
  warehouseId?: string;
}

// ============================================================================
// BatchSerialHelper
// ============================================================================

export class BatchSerialHelper {
  private rateLimiter = getSharedRateLimiter();
  private itemTypeCache: Map<string, number> = new Map();

  // --------------------------------------------------------------------------
  // ItemType classification (matches real system)
  // 0 = Standard, 4 = Batch, 5 = Batch+Serial, 6 = Serial
  // --------------------------------------------------------------------------

  static isBatchItem(itemType: number): boolean {
    return itemType === 4 || itemType === 5;
  }

  static isSerialItem(itemType: number): boolean {
    return itemType === 5 || itemType === 6;
  }

  static needsBatchTracking(itemType: number): boolean {
    return itemType === 4 || itemType === 5;
  }

  static needsSerialTracking(itemType: number): boolean {
    return itemType === 6;
  }

  static isStandardItem(itemType: number): boolean {
    return !itemType || itemType === 0;
  }

  // --------------------------------------------------------------------------
  // API calls for available batches/serials at bin
  // --------------------------------------------------------------------------

  /**
   * Get available batches for an item at a specific bin
   * Uses: GET /api/v1/stocktrans/batches/{itemNo}/bin/{warehouseId}/{binCode}
   */
  async getBatchesAtBin(itemNo: string, warehouseId: string, binCode: string): Promise<BatchInfo[]> {
    try {
      await this.rateLimiter.throttle();
      const response = await authenticatedFetch(
        `${API_BASE_URL}/stocktrans/batches/${encodeURIComponent(itemNo)}/bin/${encodeURIComponent(warehouseId)}/${encodeURIComponent(binCode)}`
      );

      if (response.ok) {
        const data = await response.json();
        return (data || []).map((b: any) => ({
          batchNo: b.batchNo,
          expiryDate: b.expiryDate || null,
          quantity: b.quantity || 0,
          binCode: b.binCode,
          warehouseId: b.warehouseId
        }));
      }
    } catch (error) {
      console.warn(`[BatchSerialHelper] Could not get batches for ${itemNo} at ${binCode}:`, error);
    }
    return [];
  }

  /**
   * Get available serial numbers for an item at a specific bin
   * Uses: GET /api/v1/stocktrans/serialnumbers/{itemNo}/bin/{warehouseId}/{binCode}
   */
  async getSerialNumbersAtBin(itemNo: string, warehouseId: string, binCode: string): Promise<SerialInfo[]> {
    try {
      await this.rateLimiter.throttle();
      const response = await authenticatedFetch(
        `${API_BASE_URL}/stocktrans/serialnumbers/${encodeURIComponent(itemNo)}/bin/${encodeURIComponent(warehouseId)}/${encodeURIComponent(binCode)}`
      );

      if (response.ok) {
        const data = await response.json();
        return (data || []).map((s: any) => ({
          serialNo: s.serialNo,
          binCode: s.binCode,
          warehouseId: s.warehouseId
        }));
      }
    } catch (error) {
      console.warn(`[BatchSerialHelper] Could not get serial numbers for ${itemNo} at ${binCode}:`, error);
    }
    return [];
  }

  // --------------------------------------------------------------------------
  // Generator methods for simulator data
  // --------------------------------------------------------------------------

  /**
   * Generate a realistic simulator batch number
   */
  static generateBatchNo(itemNo: string): string {
    const now = new Date();
    const dateStr = `${now.getFullYear()}${String(now.getMonth() + 1).padStart(2, '0')}${String(now.getDate()).padStart(2, '0')}`;
    const seq = Math.floor(Math.random() * 999) + 1;
    return `SIM-BATCH-${itemNo}-${dateStr}-${String(seq).padStart(3, '0')}`;
  }

  /**
   * Generate a realistic simulator serial number
   */
  static generateSerialNo(itemNo: string, seq: number): string {
    const ts = Date.now().toString(36).toUpperCase();
    return `SIM-SN-${itemNo}-${ts}-${String(seq).padStart(4, '0')}`;
  }

  /**
   * Generate a random expiry date 6-12 months in the future
   */
  static generateExpiryDate(): string {
    const now = new Date();
    const monthsAhead = 6 + Math.floor(Math.random() * 7); // 6-12 months
    const expiry = new Date(now.getFullYear(), now.getMonth() + monthsAhead, now.getDate());
    return expiry.toISOString();
  }

  // --------------------------------------------------------------------------
  // ItemType lookup with caching
  // --------------------------------------------------------------------------

  /**
   * Get itemType from a line's data (check multiple field names)
   */
  static getItemTypeFromLine(line: any): number {
    return line.itemType ?? line.ItemType ?? line.itemtype ?? 0;
  }

  /**
   * Cache an item type for later use
   */
  cacheItemType(itemNo: string, itemType: number): void {
    this.itemTypeCache.set(itemNo, itemType);
  }

  /**
   * Get cached item type (returns undefined if not cached)
   */
  getCachedItemType(itemNo: string): number | undefined {
    return this.itemTypeCache.get(itemNo);
  }

  /**
   * Clear the item type cache
   */
  clearCache(): void {
    this.itemTypeCache.clear();
  }
}
